import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiInstance } from 'services/api'
import { useMasquaradeToken } from 'services/masquarade/masquarade.hooks'
import { useAccessToken } from 'services/token/token.hooks'
import { isNil } from 'ramda'
import { CONTRACT_PENDING_SIGNATURE_GROUP } from '../contracts.consts'
import { LeaseContractResponse } from './useLeaseContracts'

export const BOOKING_AGREEMENT_DELIVERY_METHOD = {
  MOBILE_APP: 'mobile_app',
  EMAIL: 'email',
  PRINT: 'print'
} as const

export type BookingAgreementDeliveryMethod =
  | (typeof BOOKING_AGREEMENT_DELIVERY_METHOD)[keyof typeof BOOKING_AGREEMENT_DELIVERY_METHOD]
  | string

export const BOOKING_AGREEMENT_ACTION_TYPE = {
  CONTRACT_VIEWED: 'CONTRACT_VIEWED',
  CONTRACT_SCROLLED_END: 'CONTRACT_SCROLLED_END',
  CONTRACT_CHECKBOX_TICKED: 'CONTRACT_CHECKBOX_TICKED',
  CONTRACT_ACCEPTED: 'CONTRACT_ACCEPTED'
} as const

export type BookingAgreementActionType =
  (typeof BOOKING_AGREEMENT_ACTION_TYPE)[keyof typeof BOOKING_AGREEMENT_ACTION_TYPE]

export type BookingAgreementEmittableActionType = Extract<
  BookingAgreementActionType,
  'CONTRACT_SCROLLED_END' | 'CONTRACT_CHECKBOX_TICKED'
>

export interface BookingAgreementAttachment {
  id: number
  name: string
  file: string
  sha256?: string
  sizeBytes?: number
}

export interface BookingAgreementJourneyEvent {
  id: number
  actionType: BookingAgreementActionType
  occurredAt: string
}

export interface BookingAgreementReceipt {
  id: number
  leaseContract: number
  contractVersionId: string
  contractPdfSha256: string
  attachmentsManifest: Array<{
    attachmentId: number
    name: string
    sha256?: string
    sizeBytes?: number
  }>
  consentStatement: string
  acceptanceButtonLabel: string
  acceptanceMethod: string
  viewedAt: string | null
  scrolledToEndAt: string | null
  checkboxTickedAt: string | null
  acceptedAt: string
  ipAddress: string | null
  userAgent: string | null
  deviceModel: string | null
  osName: string | null
  osVersion: string | null
  appVersion: string | null
  sessionId: string
  recordSha256: string
  createdAt: string
  journey: BookingAgreementJourneyEvent[]
}

export interface LeaseContractsBookingAgreementResponse {
  id: number
  bookingAgreementPdf: string | null
  bookingAgreementPdfSha256?: string | null
  isWisContract?: boolean
  deliveryMethod?: BookingAgreementDeliveryMethod | null
  attachments?: BookingAgreementAttachment[]
  isAcceptanceAvailable?: boolean
  consentStatementText?: string
  acceptanceButtonLabel?: string
  alreadyAcceptedAt?: string | null
}

export interface BookingAgreementSessionResponse {
  sessionId: string
  serverTime: string
}

export interface BookingAgreementDeviceMeta {
  appVersion?: string
  deviceModel?: string
  osName?: string
  osVersion?: string
}

const baseUrl = (contractId: number) => `/lease/contracts/${contractId}/booking-agreement`

const buildAuthHeaders = (accessToken: string | null, masquaradeToken?: string | null) => ({
  authorization: `Bearer ${accessToken}`,
  'mask-authorization': !!masquaradeToken ? `Token ${masquaradeToken}` : undefined
})

export const useLeaseContractsBookingAgreement = (id: number) => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()
  const queryClient = useQueryClient()

  const bookingAgreementQueryKey = ['bookingAgreementPdf', id, masquaradeToken]
  const receiptQueryKey = ['bookingAgreementReceipt', id, masquaradeToken]

  const { data: bookingAgreementPdf, ...rest } = useQuery({
    queryKey: bookingAgreementQueryKey,
    queryFn: () =>
      apiInstance<LeaseContractsBookingAgreementResponse>({
        url: `${baseUrl(id)}/`,
        method: 'GET',
        headers: buildAuthHeaders(accessToken, masquaradeToken)
      }).then(({ data }) => data),
    enabled: !!accessToken && !!id
  })

  const refetchBookingAgreement = () =>
    queryClient.invalidateQueries({ queryKey: bookingAgreementQueryKey })

  const startSession = useMutation({
    mutationFn: (deviceMeta: BookingAgreementDeviceMeta) =>
      apiInstance<BookingAgreementSessionResponse>({
        url: `${baseUrl(id)}/start-session/`,
        method: 'POST',
        data: deviceMeta,
        headers: buildAuthHeaders(accessToken, masquaradeToken)
      }).then(({ data }) => data)
  })

  const logEvent = useMutation({
    mutationFn: ({
      sessionId,
      actionType,
      deviceMeta
    }: {
      sessionId: string
      actionType: BookingAgreementEmittableActionType
      deviceMeta: BookingAgreementDeviceMeta
    }) =>
      apiInstance<void>({
        url: `${baseUrl(id)}/events/`,
        method: 'POST',
        data: {
          sessionId,
          actionType,
          ...deviceMeta
        },
        headers: buildAuthHeaders(accessToken, masquaradeToken)
      })
  })

  const acceptAgreement = useMutation({
    mutationFn: ({
      sessionId,
      consentStatement,
      deviceMeta
    }: {
      sessionId: string
      consentStatement: string
      deviceMeta: BookingAgreementDeviceMeta
    }) =>
      apiInstance<BookingAgreementReceipt>({
        url: `${baseUrl(id)}/accept/`,
        method: 'POST',
        data: {
          sessionId,
          consentStatement,
          ...deviceMeta
        },
        headers: buildAuthHeaders(accessToken, masquaradeToken)
      }).then(({ data }) => data),
    onSuccess: (data) => {
      queryClient.setQueryData(receiptQueryKey, data)
      refetchBookingAgreement()
    }
  })

  return {
    bookingAgreementPdf,
    refetchBookingAgreement,
    startSession,
    logEvent,
    acceptAgreement,
    ...rest
  }
}

export const usePendingBookingAcceptanceContracts = () => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()

  const { data: candidates = [] } = useQuery({
    queryKey: ['leaseContractsList', masquaradeToken],
    queryFn: () =>
      apiInstance<LeaseContractResponse[]>({
        url: '/lease/contracts/',
        method: 'GET',
        headers: buildAuthHeaders(accessToken, masquaradeToken)
      }).then(({ data }) => data),
    select: (data) =>
      data.filter(
        ({ contractNumber, leaseStatus }) =>
          !isNil(contractNumber) && CONTRACT_PENDING_SIGNATURE_GROUP.includes(leaseStatus)
      ),
    enabled: !!accessToken
  })

  const bookingAgreementQueries = useQueries({
    queries: candidates.map(({ id }) => ({
      queryKey: ['bookingAgreementPdf', id, masquaradeToken],
      queryFn: () =>
        apiInstance<LeaseContractsBookingAgreementResponse>({
          url: `${baseUrl(id)}/`,
          method: 'GET',
          headers: buildAuthHeaders(accessToken, masquaradeToken)
        }).then(({ data }) => data),
      enabled: !!accessToken && !!id
    }))
  })

  const pendingContracts = candidates.filter((_, index) => {
    const bookingAgreement = bookingAgreementQueries[index]?.data
    return (
      bookingAgreement?.deliveryMethod === BOOKING_AGREEMENT_DELIVERY_METHOD.MOBILE_APP &&
      !!bookingAgreement?.isAcceptanceAvailable &&
      !bookingAgreement?.alreadyAcceptedAt
    )
  })

  return {
    pendingContracts,
    isLoading: bookingAgreementQueries.some(({ isLoading, fetchStatus }) => isLoading && fetchStatus !== 'idle')
  }
}

export const useLeaseContractsBookingAgreementReceipt = (id: number, enabled = true) => {
  const { accessToken } = useAccessToken()
  const { masquaradeToken } = useMasquaradeToken()

  const { data: receipt, ...rest } = useQuery({
    queryKey: ['bookingAgreementReceipt', id, masquaradeToken],
    queryFn: () =>
      apiInstance<BookingAgreementReceipt>({
        url: `${baseUrl(id)}/receipt/`,
        method: 'GET',
        headers: buildAuthHeaders(accessToken, masquaradeToken)
      }).then(({ data }) => data),
    enabled: !!accessToken && !!id && enabled,
    retry: (failureCount, error: any) => {
      if (error?.response?.status === 404) {
        return false
      }
      return failureCount < 2
    }
  })

  return {
    receipt,
    ...rest
  }
}
